import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './WidgetStyles.module.css';
import type { AssistantImageMap, WidgetMessage } from '../types/chat';
import type { SubmitFeedbackInput } from '../types/feedback';
import { AssistantMessageCard, type AssistantImagePreview } from './AssistantMessageCard';
import { UserMessageBubble } from './UserMessageBubble';
import { InjectedUserMessageBubble } from './InjectedUserMessageBubble';
import { ExamplePromptsPanel, type ExamplePromptsPanelProps } from './ExamplePromptsPanel';
import { usePortalContainer } from './PortalContainerContext';

const SCROLL_THRESHOLD = 8;

type ScrollState = {
    hasOverflow: boolean;
    isAtBottom: boolean;
};

export type ConversationPaneProps = {
    messages: WidgetMessage[];
    isStreaming?: boolean;
    pendingStatus?: string;
    onCopyMessage?: (content: string) => void | Promise<void>;
    examplePrompts?: ExamplePromptsPanelProps | null;
    showDuration?: boolean;
    images?: AssistantImageMap;
    isVisible?: boolean;
    onScrollIndicatorVisibilityChange?: (isVisible: boolean) => void;
    onScrollToLatestChange?: (scrollToLatest: (() => void) | null) => void;
    canSubmitLatestFeedback?: boolean;
    isSubmittingFeedback?: boolean;
    onSubmitFeedback?: (input: SubmitFeedbackInput) => Promise<boolean>;
};

export function ConversationPane({
    messages,
    isStreaming = false,
    pendingStatus = '',
    onCopyMessage,
    examplePrompts,
    showDuration = true,
    images = {},
    isVisible = true,
    onScrollIndicatorVisibilityChange,
    onScrollToLatestChange,
    canSubmitLatestFeedback = false,
    isSubmittingFeedback = false,
    onSubmitFeedback
}: ConversationPaneProps) {
    const portalContainer = usePortalContainer();
    const conversationRef = useRef<HTMLDivElement>(null);
    const conversationContentRef = useRef<HTMLDivElement>(null);
    const previousMessagesRef = useRef<WidgetMessage[]>(messages);
    const shouldStickToBottomRef = useRef(true);
    const previousIsVisibleRef = useRef(isVisible);
    const previousIsShowingExamplePromptsRef = useRef(false);
    const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
    const previewDialogRef = useRef<HTMLDivElement>(null);
    const [activeImagePreview, setActiveImagePreview] = useState<AssistantImagePreview | null>(null);
    const [scrollState, setScrollState] = useState<ScrollState>({
        hasOverflow: false,
        isAtBottom: true,
    });

    const updateScrollState = useCallback(() => {
        const container = conversationRef.current;

        if (!container) {
            return;
        }

        const { scrollHeight, clientHeight, scrollTop } = container;
        const hasOverflow = scrollHeight > clientHeight + 1;
        const isAtBottom = scrollHeight - (scrollTop + clientHeight) <= SCROLL_THRESHOLD;
        shouldStickToBottomRef.current = isAtBottom;

        setScrollState((previous) => {
            if (previous.hasOverflow === hasOverflow && previous.isAtBottom === isAtBottom) {
                return previous;
            }

            return {
                hasOverflow,
                isAtBottom,
            };
        });
    }, []);

    const isShowingExamplePrompts = messages.length === 0 && examplePrompts !== null && examplePrompts !== undefined;

    useEffect(() => {
        const container = conversationRef.current;

        if (!container) {
            return;
        }

        const handleScroll = () => updateScrollState();

        updateScrollState();
        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [updateScrollState]);

    const handleScrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        const container = conversationRef.current;

        if (!container) {
            return;
        }

        if (typeof container.scrollTo === 'function') {
            container.scrollTo({
                top: container.scrollHeight,
                behavior,
            });
            return;
        }

        container.scrollTop = container.scrollHeight;
    }, []);

    const handleScrollToLatest = useCallback(() => {
        handleScrollToBottom('smooth');
    }, [handleScrollToBottom]);

    useEffect(() => {
        const previousMessages = previousMessagesRef.current;
        const hasNewUserOutboundMessage =
            messages.length > previousMessages.length &&
            messages
                .slice(previousMessages.length)
                .some((message) => message.role === 'user' && !message.isInjectedUserMessage);

        previousMessagesRef.current = messages;

        const shouldForceScrollToBottom =
            previousIsShowingExamplePromptsRef.current && !isShowingExamplePrompts && messages.length > 0;

        if (hasNewUserOutboundMessage) {
            shouldStickToBottomRef.current = true;
            handleScrollToBottom('auto');
            updateScrollState();
            return;
        }

        updateScrollState();

        if (shouldForceScrollToBottom) {
            shouldStickToBottomRef.current = true;
            handleScrollToBottom('auto');
            return;
        }

        if (shouldStickToBottomRef.current) {
            handleScrollToBottom('auto');
        }
    }, [isShowingExamplePrompts, messages, handleScrollToBottom, updateScrollState]);

    useEffect(() => {
        const wasVisible = previousIsVisibleRef.current;
        const wasShowingExamplePrompts = previousIsShowingExamplePromptsRef.current;

        previousIsVisibleRef.current = isVisible;
        previousIsShowingExamplePromptsRef.current = isShowingExamplePrompts;

        if (!isVisible || !isShowingExamplePrompts) {
            return;
        }

        const becameVisible = !wasVisible;
        const enteredExamplePromptState = !wasShowingExamplePrompts;

        if (!becameVisible && !enteredExamplePromptState) {
            return;
        }

        const container = conversationRef.current;

        if (!container) {
            return;
        }

        shouldStickToBottomRef.current = false;

        if (typeof container.scrollTo === 'function') {
            container.scrollTo({
                top: 0,
                behavior: 'auto'
            });
        } else {
            container.scrollTop = 0;
        }

        updateScrollState();
    }, [isShowingExamplePrompts, isVisible, updateScrollState]);

    useEffect(() => {
        const content = conversationContentRef.current;
        if (!content || typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            if (shouldStickToBottomRef.current) {
                handleScrollToBottom('auto');
            }
            updateScrollState();
        });

        resizeObserver.observe(content);

        return () => resizeObserver.disconnect();
    }, [handleScrollToBottom, updateScrollState]);

    const handleImageLoad = useCallback(() => {
        if (!shouldStickToBottomRef.current) {
            return;
        }

        handleScrollToBottom('auto');
        updateScrollState();
    }, [handleScrollToBottom, updateScrollState]);

    const handleImagePreviewRequested = useCallback((preview: AssistantImagePreview) => {
        previouslyFocusedElementRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setActiveImagePreview(preview);
    }, []);

    const handleCloseImagePreview = useCallback(() => {
        setActiveImagePreview(null);
        previouslyFocusedElementRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!activeImagePreview) {
            return;
        }

        previewDialogRef.current?.focus();
    }, [activeImagePreview]);

    useEffect(() => {
        if (!activeImagePreview) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            handleCloseImagePreview();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeImagePreview, handleCloseImagePreview]);

    const shouldShowScrollButton = scrollState.hasOverflow && !scrollState.isAtBottom;

    useEffect(() => {
        onScrollIndicatorVisibilityChange?.(shouldShowScrollButton);
    }, [onScrollIndicatorVisibilityChange, shouldShowScrollButton]);

    useEffect(() => {
        onScrollToLatestChange?.(handleScrollToLatest);

        return () => {
            onScrollToLatestChange?.(null);
        };
    }, [handleScrollToLatest, onScrollToLatestChange]);

    return (
        <div
            ref={conversationRef}
            className={styles.conversationPane}
            aria-live="polite"
            aria-label="Conversation history"
        >
            <div ref={conversationContentRef} className={styles.conversationContent}>
                {isShowingExamplePrompts && examplePrompts ? <ExamplePromptsPanel {...examplePrompts} /> : null}
                <ol className={styles.messageList}>
                    {messages.map((message, index) => (
                        <MessageCard
                            key={message.id}
                            message={message}
                            isPending={isStreaming && isPendingAssistant(messages, index)}
                            statusMessage={
                                isStreaming && isPendingAssistant(messages, index) ? pendingStatus : undefined
                            }
                            onCopy={onCopyMessage}
                            showDuration={showDuration}
                            images={images}
                            onImagePreviewRequested={handleImagePreviewRequested}
                            onImageLoad={handleImageLoad}
                            showFeedback={canSubmitLatestFeedback && isLatestAssistantMessage(messages, index)}
                            isSubmittingFeedback={isSubmittingFeedback}
                            onSubmitFeedback={onSubmitFeedback}
                        />
                    ))}
                </ol>
            </div>
            {activeImagePreview && portalContainer
                ? createPortal(
                      <div
                          className={styles.imagePreviewOverlay}
                          role="dialog"
                          aria-modal="true"
                          tabIndex={-1}
                          ref={previewDialogRef}
                          onClick={handleCloseImagePreview}
                      >
                          <img
                              className={styles.imagePreviewImage}
                              src={activeImagePreview.src}
                              alt={activeImagePreview.alt}
                              onClick={handleCloseImagePreview}
                          />
                      </div>,
                      portalContainer
                  )
                : null}
        </div>
    );
}

type MessageCardProps = {
    message: WidgetMessage;
    isPending?: boolean;
    statusMessage?: string;
    onCopy?: (content: string) => void | Promise<void>;
    showDuration?: boolean;
    images: AssistantImageMap;
    onImagePreviewRequested?: (preview: AssistantImagePreview) => void;
    onImageLoad?: () => void;
    showFeedback?: boolean;
    isSubmittingFeedback?: boolean;
    onSubmitFeedback?: (input: SubmitFeedbackInput) => Promise<boolean>;
};

function MessageCard({
    message,
    isPending = false,
    statusMessage,
    onCopy,
    showDuration = true,
    images,
    onImagePreviewRequested,
    onImageLoad,
    showFeedback = false,
    isSubmittingFeedback = false,
    onSubmitFeedback
}: MessageCardProps) {
    const isUserMessage = message.role === 'user';

    if (isUserMessage) {
        if (message.isInjectedUserMessage) {
            return <InjectedUserMessageBubble content={message.content} />;
        }

        return <UserMessageBubble content={message.content} />;
    }

    if (message.isAuthError) {
        return <AuthErrorMessageCard content={message.content} />;
    }

    if (!isPending && (!statusMessage || !statusMessage.trim().length) && !message.content.trim().length) {
        return null;
    }

    return (
        <AssistantMessageCard
            content={message.content}
            isPending={isPending}
            statusMessage={statusMessage}
            onCopy={onCopy}
            durationMs={message.durationMs}
            showDuration={showDuration}
            images={images}
            onImagePreviewRequested={onImagePreviewRequested}
            onImageLoad={onImageLoad}
            showFeedback={showFeedback}
            isSubmittingFeedback={isSubmittingFeedback}
            onSubmitFeedback={onSubmitFeedback}
        />
    );
}

type AuthErrorMessageCardProps = {
    content: string;
};

function AuthErrorMessageCard({ content }: AuthErrorMessageCardProps) {
    return (
        <li className={styles.authErrorMessageCard} role="alert" aria-live="polite">
            <span className={styles.authErrorIcon} aria-hidden="true">
                ⚠
            </span>
            <span className={styles.authErrorMessageText}>{content}</span>
        </li>
    );
}

function isPendingAssistant(messages: WidgetMessage[], index: number): boolean {
    const current = messages[index];
    if (!current || current.role !== 'assistant') {
        return false;
    }

    for (let i = index + 1; i < messages.length; i += 1) {
        if (messages[i].role === 'assistant') {
            return false;
        }
    }

    return !current.content || current.content.trim().length === 0;
}

function isLatestAssistantMessage(messages: WidgetMessage[], index: number): boolean {
    const current = messages[index];
    if (!current || current.role !== 'assistant' || !current.content.trim().length || current.feedbackSubmitted) {
        return false;
    }

    for (let i = index + 1; i < messages.length; i += 1) {
        if (messages[i].role === 'assistant') {
            return false;
        }
    }

    return true;
}
